import { getLstToken, getLstPhone } from "@/utils/lstSessionStorage";
import { APP_VERSION } from "@/config";
import { getUserInfo, postHallTradeReport } from "@/api/lst";
export const HALL_DATA_PUBLIC_KEY = "lst_hall_data_public";
export const HALL_QUEUE_KEY = "lst_hall_trade_queue";
export const HALL_SENT_KEY = "lst_hall_sent_markers";
const BACKOFF_MS = [4e3, 12e3, 3e4, 6e4, 12e4, 3e5, 6e5];
function backoffMs(attempts) {
    return BACKOFF_MS[Math.min(Math.max(attempts, 1), BACKOFF_MS.length) - 1];
}
function readQueue() {
    try {
        const raw = localStorage.getItem(HALL_QUEUE_KEY);
        const a = raw ? JSON.parse(raw) : [];
        return Array.isArray(a) ? a : [];
    }
    catch {
        return [];
    }
}
function writeQueue(q) {
    try {
        localStorage.setItem(HALL_QUEUE_KEY, JSON.stringify(q));
    }
    catch {
    }
}
function getSentMap() {
    try {
        const o = JSON.parse(localStorage.getItem(HALL_SENT_KEY) || "{}");
        return o && typeof o === "object" ? o : {};
    }
    catch {
        return {};
    }
}
function sentKey(orderNo, side) {
    return `${orderNo}:${side}`;
}
function beijingYmdFromMs(ms) {
    const f = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Shanghai",
        year: "numeric",
        month: "2-digit",
        day: "2-digit"
    });
    const p = {};
    for (const x of f.formatToParts(new Date(ms))) {
        if (x.type !== "literal")
            p[x.type] = x.value;
    }
    return `${p.year}-${p.month}-${p.day}`;
}
export function isTodayBeijingWallMs(ms) {
    return beijingYmdFromMs(ms) === beijingYmdFromMs(Date.now());
}
export function isHallDataPublic() {
    const v = localStorage.getItem(HALL_DATA_PUBLIC_KEY);
    return v !== "0";
}
export function setHallDataPublic(on) {
    localStorage.setItem(HALL_DATA_PUBLIC_KEY, on ? "1" : "0");
}
function isOrderSideSent(orderNo, side) {
    return !!getSentMap()[sentKey(orderNo, side)];
}
function markOrderSideSent(orderNo, side) {
    const m = getSentMap();
    m[sentKey(orderNo, side)] = 1;
    const keys = Object.keys(m);
    if (keys.length > 2500) {
        keys.sort();
        for (let i = 0; i < keys.length - 1800; i++)
            delete m[keys[i]];
    }
    try {
        localStorage.setItem(HALL_SENT_KEY, JSON.stringify(m));
    }
    catch {
    }
}
function queueHas(orderNo, side) {
    return readQueue().some((x) => x.orderNo === orderNo && x.side === side);
}
export async function ensureHallProfileLoaded() {
    try {
        const raw = localStorage.getItem("lst_profile_cache");
        if (raw) {
            const j = JSON.parse(raw);
            if (j && j.userId)
                return { userName: String(j.userName || ""), userId: String(j.userId) };
        }
    }
    catch {
    }
    const token = getLstToken();
    const phone = getLstPhone();
    if (!token || !phone)
        return null;
    const res = await getUserInfo(token, phone);
    if (Number(res.code) === 1 && res.data) {
        const p = {
            userName: String(res.data.userName || ""),
            userId: String(res.data.userId != null ? res.data.userId : "")
        };
        if (p.userId) {
            try {
                localStorage.setItem("lst_profile_cache", JSON.stringify(p));
            }
            catch {
            }
            return p;
        }
    }
    return null;
}
export async function enqueueHallTradeEvent({ side, orderNo, amountYuan, tradeTimeMs, albumId, albumName }) {
    if (!isHallDataPublic())
        return;
    const on = String(orderNo || "").trim();
    if (!on)
        return;
    const amt = Number(amountYuan);
    if (!Number.isFinite(amt) || amt <= 0)
        return;
    if (side !== "buy" && side !== "sell")
        return;
    if (isOrderSideSent(on, side) || queueHas(on, side))
        return;
    const prof = await ensureHallProfileLoaded();
    if (!prof?.userId)
        return;
    const item = {
        localId: typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
        side,
        orderNo: on,
        userName: prof.userName,
        userId: prof.userId,
        amountYuan: amt,
        tradeTimeMs: tradeTimeMs || Date.now(),
        albumId: albumId != null ? String(albumId) : "",
        albumName: albumName != null ? String(albumName) : "",
        attempts: 0,
        nextTryAt: 0
    };
    const q = readQueue();
    q.push(item);
    writeQueue(q);
    scheduleHallFlushSoon();
}
function mapItemToTrade(item) {
    return {
        userName: item.userName,
        userId: item.userId,
        side: item.side,
        amount: item.amountYuan,
        tradeTime: item.tradeTimeMs,
        orderNo: item.orderNo,
        albumId: item.albumId || void 0,
        albumName: item.albumName || void 0,
        appVersion: APP_VERSION,
        clientRef: item.localId
    };
}
function removeFromQueueByLocalId(localId) {
    writeQueue(readQueue().filter((x) => x.localId !== localId));
}
function bumpItem(item, errMsg) {
    const attempts = (item.attempts || 0) + 1;
    const nextTryAt = Date.now() + backoffMs(attempts);
    const q = readQueue().map((x) => x.localId === item.localId ? { ...x, attempts, nextTryAt, lastError: String(errMsg || "") } : x);
    writeQueue(q);
}
let flushTimer = null;
export function scheduleHallFlushSoon() {
    if (flushTimer != null)
        return;
    flushTimer = setTimeout(() => {
        flushTimer = null;
        void flushHallTradeQueueOnce();
    }, 500);
}
export async function flushHallTradeQueueOnce() {
    if (!isHallDataPublic())
        return;
    const q = readQueue();
    if (!q.length)
        return;
    const now = Date.now();
    const item = q.find((x) => (x.nextTryAt || 0) <= now);
    if (!item)
        return;
    try {
        const res = await postHallTradeReport([mapItemToTrade(item)]);
        if (res && res.success && Number(res.accepted) >= 1) {
            removeFromQueueByLocalId(item.localId);
            markOrderSideSent(item.orderNo, item.side);
            scheduleHallFlushSoon();
            return;
        }
        bumpItem(item, res?.message || "\u670D\u52A1\u7AEF\u672A\u786E\u8BA4");
    }
    catch (e) {
        bumpItem(item, e?.message || "\u7F51\u7EDC\u5F02\u5E38");
    }
    scheduleHallFlushSoon();
}
let schedulerStop = null;
export function startHallTradeScheduler() {
    if (schedulerStop)
        return schedulerStop;
    const tick = () => {
        void flushHallTradeQueueOnce();
    };
    const iv = setInterval(tick, 28e3);
    const onVis = () => {
        if (document.visibilityState === "visible")
            tick();
    };
    document.addEventListener("visibilitychange", onVis);
    tick();
    schedulerStop = () => {
        clearInterval(iv);
        document.removeEventListener("visibilitychange", onVis);
        schedulerStop = null;
    };
    return schedulerStop;
}
export function stopHallTradeScheduler() {
    schedulerStop?.();
}
